import type { AuthContextProps } from 'react-oidc-context';

/**
 * Drop a dead session and bounce to the Keycloak login.
 *
 * `removeUser()` runs FIRST, and it does two necessary things: it clears the
 * stale user from localStorage (so a dead refresh token can't survive a reload
 * and re-arm the same failure) and it stops the silent-renew timer
 * (oidc-client-ts's SilentRenewService subscribes its `_stop` to `userUnloaded`),
 * so the renew can't re-fire mid-redirect.
 *
 * Trade-off in that ordering: if Keycloak itself is unreachable the user loses
 * an already-unusable session and sees a browser error rather than a banner.
 * Redirecting first would preserve the session on failure, but then nothing
 * stops the renew timer and the "reload doesn't clear it" bug survives — which
 * is the whole point of this helper. Pure timeouts don't reach here anyway:
 * oidc-client-ts retries `ErrorTimeout` internally before surfacing an error.
 *
 * The module-level guard collapses concurrent callers (several in-flight 401s)
 * into one redirect. A successful `signinRedirect` navigates away, so the flag
 * is reset by the page load; we only clear it by hand when the redirect never
 * happened, so a later attempt can still retry.
 */
let restarting = false;

export async function restartLogin(auth: AuthContextProps, returnTo?: string): Promise<void> {
  if (restarting) {
    return;
  }
  restarting = true;
  try {
    try {
      await auth.removeUser();
    } catch {
      // Best-effort. Clearing storage is hygiene; the REDIRECT is what actually
      // recovers the user, so a storage failure must never block it.
    }
    await auth.signinRedirect(returnTo === undefined ? undefined : { state: { returnTo } });
  } catch {
    restarting = false;
  }
}

/**
 * Test-only: reset the once-guard between cases. Under test `signinRedirect` is
 * a mock that RESOLVES instead of navigating away, so without this the first
 * case to call `restartLogin` latches the guard and every later case silently
 * skips its redirect while still appearing to pass.
 */
export function resetRestartLogin(): void {
  restarting = false;
}
